import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { usePlatformAdmin } from '../../../hooks/usePlatformAdmin';
import {
  getSubscriberSummary,
  getChannelAudience,
  exportSubscribers,
  listChannelInvites,
  createChannelInvite,
  revokeChannelInvite,
  resendChannelInvite,
  rotateShareToken,
} from '../../../api/marketplaceApi';
import type { Channel, ChannelVisibility, ChannelInvite, SubscriberSummary, AudienceSummary } from 'types/channel';
import VelocitySparklineAtom from '../../atoms/VelocitySparkline/VelocitySparklineAtom';
import type { VelocityDataPoint } from '../../atoms/VelocitySparkline/VelocitySparklineAtom';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import ChannelVisibilityAccessMolecule from '../../molecules/marketplace/ChannelVisibilityAccessMolecule';
import SubscriberBreakdownMolecule from '../../molecules/marketplace/SubscriberBreakdownMolecule';
import SubscribersDrawerOrganism from './SubscribersDrawerOrganism';
import './ChannelAudienceSectionOrganism.scss';

// ============================================================
// Types
// ============================================================

export interface AudienceDraft {
  visibility: ChannelVisibility;
  isFeatured: boolean;
}

export interface ChannelAudienceSectionOrganismProps {
  channel: Channel;
  draft: AudienceDraft;
  update: (patch: Partial<AudienceDraft>) => void;
  organizationId: string;
}

// ============================================================
// Helpers
// ============================================================

/** Map weekly subscriber counts to VelocityDataPoint scores (-100..+100). */
function weeklyCountsToDataPoints(weekly: number[]): VelocityDataPoint[] {
  const max = Math.max(...weekly, 1);
  return weekly.map((count) => ({
    score: Math.round((count / max) * 100) - 50,
  }));
}

// ============================================================
// Component
// ============================================================

const ChannelAudienceSectionOrganism: React.FC<ChannelAudienceSectionOrganismProps> = ({
  channel,
  draft,
  update,
  organizationId,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { isPlatformAdmin } = usePlatformAdmin();
  const ns = 'marketplace.channelManage.v3Audience';

  const [summary, setSummary] = useState<SubscriberSummary | null>(null);
  const [audience, setAudience] = useState<AudienceSummary | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [invites, setInvites] = useState<ChannelInvite[]>([]);
  const [isMutating, setIsMutating] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(channel.share_token ?? null);

  // Fetch subscriber summary + invites on mount
  useEffect(() => {
    if (!apiClients) return;
    void getSubscriberSummary(apiClients.schemaClient, channel.id).then(setSummary).catch(() => null);
    void listChannelInvites(apiClients.schemaClient, channel.id).then(setInvites).catch(() => []);
    void getChannelAudience(apiClients.schemaClient, channel.id).then(setAudience).catch(() => null);
  }, [channel.id, apiClients]);

  const handleExport = useCallback(async () => {
    if (!apiClients) return;
    try {
      const blob = await exportSubscribers(apiClients.schemaClient, channel.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscribers-${channel.system_name}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Non-fatal: export failure doesn't break the page
    }
  }, [apiClients, channel.id, channel.system_name]);

  const handleRotateToken = useCallback(async () => {
    if (!apiClients) return;
    setIsMutating(true);
    try {
      const response = await rotateShareToken(apiClients.schemaClient, channel.id);
      setShareToken(response.token);
    } finally {
      setIsMutating(false);
    }
  }, [apiClients, channel.id]);

  const handleInvite = useCallback(
    async (email: string) => {
      if (!apiClients) return;
      setIsMutating(true);
      try {
        const invite = await createChannelInvite(apiClients.schemaClient, channel.id, email);
        setInvites((prev) => [...prev, invite]);
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, channel.id]
  );

  const handleRevokeInvite = useCallback(
    async (inviteId: string) => {
      if (!apiClients) return;
      setIsMutating(true);
      try {
        await revokeChannelInvite(apiClients.schemaClient, channel.id, inviteId);
        setInvites((prev) => prev.filter((i) => i.id !== inviteId));
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, channel.id]
  );

  const handleResendInvite = useCallback(
    async (inviteId: string) => {
      if (!apiClients) return;
      setIsMutating(true);
      try {
        const updated = await resendChannelInvite(apiClients.schemaClient, channel.id, inviteId);
        setInvites((prev) => prev.map((i) => (i.id === inviteId ? updated : i)));
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, channel.id]
  );

  const nonZeroWeekCount = summary ? summary.weekly.filter(v => v > 0).length : 0;
  const dataPoints: VelocityDataPoint[] = nonZeroWeekCount >= 2 && summary
    ? weeklyCountsToDataPoints(summary.weekly)
    : [];

  return (
    <>
    {audience && (
      <SubscribersDrawerOrganism
        visible={drawerOpen}
        onHide={() => setDrawerOpen(false)}
        audience={audience}
        channelId={channel.id}
      />
    )}
    <div className="channel-audience-section-organism">
      {/* ── Subscribers card ── */}
      <section className="channel-audience-section-organism__card" aria-labelledby="aud-subscribers-heading">
        <h3 id="aud-subscribers-heading" className="channel-audience-section-organism__card-title">
          {t(`${ns}.subscribers.title`)}
        </h3>

        <p className="channel-audience-section-organism__subtitle">
          {t(`${ns}.subscribers.subtitle`)}
        </p>

        <div className="channel-audience-section-organism__header-row">
          <div className="channel-audience-section-organism__stats-group">
            <div className="channel-audience-section-organism__stat-primary">
              <span className="channel-audience-section-organism__stat-label">
                {t(`${ns}.subscribers.totalLabel`)}
              </span>
              <div className="channel-audience-section-organism__stat-display">
                <span className="channel-audience-section-organism__stat-value">
                  {summary ? summary.total.toLocaleString() : '—'}
                </span>
                {summary?.deltaThisWeek !== undefined && (
                  <span className="channel-audience-section-organism__delta-indicator">
                    {summary.deltaThisWeek >= 0 ? '+' : ''}{summary.deltaThisWeek} {t(`${ns}.subscribers.deltaThisWeek`)}
                  </span>
                )}
              </div>
            </div>

            <div className="channel-audience-section-organism__actions">
              <button
                type="button"
                className="channel-audience-section-organism__export-btn"
                onClick={() => void handleExport()}
                aria-label={t(`${ns}.subscribers.export`)}
              >
                <i className="pi pi-download" aria-hidden="true" />
                {t(`${ns}.subscribers.export`)}
              </button>
            </div>
          </div>

          {nonZeroWeekCount >= 2 && (
            <VelocitySparklineAtom
              dataPoints={dataPoints}
              areaFill
              hue={channel.hue ?? 210}
              height={80}
              className="channel-audience-section-organism__sparkline"
            />
          )}
        </div>

        <hr className="channel-audience-section-organism__divider" aria-hidden="true" />

        {audience && (
          <SubscriberBreakdownMolecule
            byType={audience.byType}
            restrictedCount={audience.restrictedCount}
            onViewAll={() => setDrawerOpen(true)}
          />
        )}
      </section>

      {/* ── Visibility & Access card ── */}
      <section className="channel-audience-section-organism__card" aria-labelledby="aud-visibility-heading">
        <h3 id="aud-visibility-heading" className="channel-audience-section-organism__card-title">
          {t(`${ns}.visibility.sectionTitle`)}
        </h3>

        <ChannelVisibilityAccessMolecule
          visibility={draft.visibility}
          shareToken={shareToken}
          invites={invites}
          onVisibilityChange={(v) => update({ visibility: v })}
          onRotateToken={handleRotateToken}
          onInvite={handleInvite}
          onRevokeInvite={handleRevokeInvite}
          onResendInvite={handleResendInvite}
          isMutating={isMutating}
        />

        {/* ── Public settings (Featured toggle + standard-behaviors notice) — public only ── */}
        {draft.visibility === 'public' && (
          <div className="channel-audience-section-organism__public-settings">
            <div className="channel-audience-section-organism__featured-row">
              <div className="channel-audience-section-organism__featured-label-group">
                <span className="channel-audience-section-organism__featured-title">
                  {t(`${ns}.featured.title`)}
                  {!isPlatformAdmin && (
                    <StatusBadgeAtom
                      tone="neutral"
                      label={t(`${ns}.featured.adminOnly`)}
                      size="small"
                      className="channel-audience-section-organism__admin-only-badge"
                    />
                  )}
                </span>
                <span className="channel-audience-section-organism__featured-desc">
                  {isPlatformAdmin
                    ? t(`${ns}.featured.descriptionAdmin`)
                    : t(`${ns}.featured.descriptionNonAdmin`)}
                </span>
              </div>
              <SwitchAtom
                inputId="aud-featured-toggle"
                checked={draft.isFeatured}
                disabled={!isPlatformAdmin}
                onChange={(v) => update({ isFeatured: v })}
              />
            </div>

            <div className="channel-audience-section-organism__public-settings-notice">
              <i className="pi pi-info-circle channel-audience-section-organism__public-settings-notice-icon" aria-hidden="true" />
              <span>{t(`${ns}.publicSettings.standardBehaviorsNotice`)}</span>
            </div>
          </div>
        )}
      </section>
    </div>
    </>
  );
};

ChannelAudienceSectionOrganism.displayName = 'ChannelAudienceSectionOrganism';

export default ChannelAudienceSectionOrganism;
